// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { newGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Logger from "@insite/client-framework/Logger";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getPageLinkByNodeId, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Link, { LinkPresentationProps, StyledClickable } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Icons from "@mcmc/XDL/Icons/mcmcCommonIcons";
import { Roboto } from "@mcmc/XDL/Utilities/sharedStyles";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    links = "links",
    customCSS = "customCSS",
}

export interface HeaderLinkModel {
    fields: {
        openInNewWindow: boolean;
        overriddenTitle: string;
        destination: LinkFieldValue;
        phone: string;
        linkType: string;
        icon: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: HeaderLinkModel[];
        [fields.customCSS]: string;
    };
    extendedStyles?: HeaderLinkListStyles;
}

export interface HeaderLinkListStyles {
    headerLinkListWrapper?: InjectableCss;
    link?: LinkPresentationProps;
}

export const headerLinkListStyles: HeaderLinkListStyles = {
    headerLinkListWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-end;
            ${StyledClickable} {
                margin-left: 40px;

                &:first-child {
                    margin-left: 0;
                }
            }
        `,
    },
    link: {
        color: "navigation.secondary.text.main",
        hoverStyle: {
            color: "navigation.secondary.text.hover",
        },
        typographyProps: {
            ...Roboto,
            weight: 400,
            css: css`
                letter-spacing: 0;
            `,
        },
    },
};

const defaultCustomCss = `.list-wrapper {
}

.list-link {
}
`;

const mapDispatchToProps = {
    setNavDrawerIsOpen,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export const HeaderLinkList: React.FC<Props> = ({ fields, setNavDrawerIsOpen, extendedStyles }) => {
    const links = useGetLinks(fields.links, o => o.fields.destination);
    const styles = useMergeStyles("headerLinkList", headerLinkListStyles, extendedStyles);

    if (links.length < 1) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="list-wrapper" {...styles.headerLinkListWrapper}>
                {links.map((link, index) => {
                    const { openInNewWindow, overriddenTitle, linkType, phone, icon } = fields.links[index].fields;

                    const forwardProps: {
                        onClick?: React.EventHandler<React.MouseEvent>;
                        href?: string;
                        target?: string;
                        id?: string;
                    } = {};

                    if (linkType === "phone") {
                        forwardProps.href = `tel:${phone}`;
                        forwardProps.onClick = e => {
                            e.defaultPrevented = true; // fix OOTB issue, the telephone/email links not opening
                        };
                    } else if (linkType === "chat") {
                        forwardProps.onClick = () => {
                            setNavDrawerIsOpen({ navDrawerIsOpen: false });
                            (window as any).LC_API?.open_chat_window();
                        };
                        forwardProps.id = "chatheader";
                    } else {
                        forwardProps.href = link.url;
                        forwardProps.target = openInNewWindow ? "_blank" : "";
                    }
                    return (
                        <Link
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            {...forwardProps}
                            {...styles.link}
                            className="list-link"
                            icon={{
                                iconProps: { src: Icons[icon], size: 14, ...styles.link?.icon?.iconProps, id: `${forwardProps.id}_icon_wrapper` },
                                position: styles.link?.icon?.position || "right",
                            }}
                            typographyProps={{
                                ...styles.link?.typographyProps,
                                id: `${forwardProps.id}_title_text`
                            }}
                        >
                            {overriddenTitle || link.title}
                        </Link>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};
const phoneRegexp = new RegExp(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im);

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(HeaderLinkList),
    definition: {
        group: "Header",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    const linkType = item.fields.linkType;
                    const phone = item.fields.phone;

                    if (linkType === "link") {
                        if (type === "Page") {
                            const link = getPageLinkByNodeId(state, value);
                            return link?.title;
                        }
                        if (type === "Category") {
                            let displayValue = "";
                            const categories = (state as any).pageEditor.categories;
                            if (!categories) {
                                displayValue = "Loading...";
                            } else {
                                const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                                if (matchingCategories.length === 0) {
                                    displayValue = "Unknown";
                                } else {
                                    displayValue = matchingCategories[0].shortDescription;
                                }
                            }

                            return displayValue;
                        }
                        if (type === "Url") {
                            return value;
                        }
                        return value;
                    }
                    return phone;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "linkType",
                        displayName: "Link Type",
                        editorTemplate: "RadioButtonsField",
                        options: [
                            {
                                displayName: "Link",
                                value: "link",
                            },
                            {
                                displayName: "Phone #",
                                value: "phone",
                            },
                            {
                                displayName: "Chat",
                                value: "chat",
                            },
                        ],
                        isRequired: true,
                        defaultValue: "link",
                    },
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        isVisible: (item?: HasFields) => {
                            return item?.fields.linkType === "link";
                        },
                    },
                    {
                        name: "phone",
                        displayName: "Phone #",
                        editorTemplate: "TextField",
                        regularExpression: phoneRegexp,
                        isRequired: true,
                        defaultValue: "",
                        isVisible: (item?: HasFields) => {
                            return item?.fields.linkType === "phone";
                        },
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "icon",
                        displayName: "Icon",
                        editorTemplate: "DropDownField",
                        options: Object.keys(Icons).map(key => {
                            return { displayName: key, value: key };
                        }),
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                        isVisible: (item?: HasFields) => {
                            return item?.fields.linkType === "link";
                        },
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
